import { useCallback, useEffect, useState } from "react";
import { History, Plus, RefreshCw, RotateCw, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { toast } from "sonner";

const formatWhen = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const WATCH_KEY = "mindforge_face_run";

const STATE_WORDS = {
  running: "Running",
  complete: "Done",
  partial: "Stopped early",
  failed: "Failed",
  interrupted: "Stopped by restart",
  stalled: "Stopped responding",
};

const STATE_TONE = {
  running: "text-primary",
  complete: "text-emerald-400",
  partial: "text-amber-400",
  failed: "text-destructive",
  interrupted: "text-amber-400",
  stalled: "text-amber-400",
};

/** Generating faces, with the run visible from start to verdict. */
const FaceGenerationPanel = ({ options, onChanged, onReview }) => {
  const [region, setRegion] = useState("uk");
  const [complexity, setComplexity] = useState("simple");
  const [count, setCount] = useState(10);
  const [plan, setPlan] = useState(null);
  const [batch, setBatch] = useState(null);
  const [faces, setFaces] = useState([]);
  const [awaiting, setAwaiting] = useState(0);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [openRun, setOpenRun] = useState(null);
  const [runFaces, setRunFaces] = useState([]);
  const [starting, setStarting] = useState(false);

  const regionLabel = (key) => options?.regions?.find((r) => r.key === key)?.label || key;
  const complexityLabel = complexity === "simple" ? "simple" : "complex";

  const loadPlan = useCallback(async () => {
    const res = await authFetch(`${API}/admin/faces/topup/plan?region=${region}`);
    if (res.ok) setPlan(await res.json());
  }, [region]);

  const loadHistory = useCallback(async () => {
    const res = await authFetch(`${API}/admin/faces/batches`);
    if (res.ok) setHistory((await res.json()).batches || []);
  }, []);

  const loadBatch = useCallback(async (batchId) => {
    const res = await authFetch(`${API}/admin/faces/batches/${batchId}`);
    if (!res.ok) return;
    const data = await res.json();
    setBatch(data.batch);
    setFaces(data.faces || []);
    setAwaiting(data.awaiting_review || 0);
  }, []);

  // Read the run in progress from the record, so a refresh or a server
  // restart never loses sight of it. The last run started in this browser is
  // remembered too, so its verdict survives one refresh.
  useEffect(() => {
    (async () => {
      const res = await authFetch(`${API}/admin/faces/batches/active`);
      const active = res.ok ? (await res.json()).batch : null;
      const watched = localStorage.getItem(WATCH_KEY);
      if (active) loadBatch(active.batch_id);
      else if (watched) loadBatch(watched);
    })();
    loadHistory();
  }, [loadBatch, loadHistory]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  useEffect(() => {
    if (!batch?.running) return;
    const t = setInterval(() => {
      loadBatch(batch.batch_id);
      onChanged?.();
    }, 4000);
    return () => clearInterval(t);
  }, [batch, loadBatch, onChanged]);

  // While nothing is on screen, keep asking whether a run is going at all, so
  // a run started elsewhere — or one the first check missed — still appears.
  useEffect(() => {
    if (batch?.running) return;
    const t = setInterval(async () => {
      const res = await authFetch(`${API}/admin/faces/batches/active`);
      if (!res.ok) return;
      const active = (await res.json()).batch;
      if (active) loadBatch(active.batch_id);
    }, 10000);
    return () => clearInterval(t);
  }, [batch, loadBatch]);

  // When a run finishes, refresh the history so it is recorded there too.
  useEffect(() => {
    if (batch && !batch.running) {
      loadHistory();
      loadPlan();
    }
  }, [batch?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (url, body) => {
    if (starting) return;
    setStarting(true);
    const res = await authFetch(url, {
      method: "POST",
      ...(body
        ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
        : {}),
    });
    const data = await res.json().catch(() => ({}));
    setStarting(false);
    if (!res.ok) {
      toast.error(data.detail || "Could not start the run");
      return;
    }
    if (!data.batch_id) {
      toast.message(data.message || data.sentence);
      return;
    }
    toast.success(`Run started — ${data.requested} faces, awaiting review when done`);
    localStorage.setItem(WATCH_KEY, data.batch_id);
    // Drawn from the press itself, so the in-progress state never depends on a
    // follow-up request landing first.
    const label = data.region_label || regionLabel(data.region || region);
    setBatch({
      batch_id: data.batch_id,
      requested: data.requested,
      created: 0,
      failed: 0,
      percent: 0,
      status: "running",
      running: true,
      region: data.region || region,
      region_label: label,
      headline: `Generating ${data.requested} ${label} faces — starting…`,
    });
    setFaces([]);
    setAwaiting(0);
    loadBatch(data.batch_id);
    loadHistory();
    onChanged?.();
  };

  const generate = () =>
    start(`${API}/admin/faces/generate`, {
      count: Number(count),
      region,
      complexity,
    });

  const topUp = () =>
    start(`${API}/admin/faces/topup?region=${region}&complexity=${complexity}`);

  const retry = (batchId) => start(`${API}/admin/faces/batches/${batchId}/retry`);

  const openRunFaces = async (batchId) => {
    if (openRun === batchId) {
      setOpenRun(null);
      setRunFaces([]);
      return;
    }
    const res = await authFetch(`${API}/admin/faces/batches/${batchId}`);
    if (res.ok) {
      const data = await res.json();
      setOpenRun(batchId);
      setRunFaces(data.faces || []);
    }
  };

  return (
    <div className="mb-6 border border-border" id="face-generation-panel" data-testid="face-generation-panel">
      {/* Progress — the first thing on the screen while a run is going */}
      {batch && (
        <div
          className={`p-4 border-b border-border ${
            batch.running ? "bg-primary/5" : "bg-muted/40"
          }`}
          data-testid="face-run-progress"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm" data-testid="face-run-headline">
                {batch.headline}
              </p>
              <p className="text-xs text-muted-foreground">
                {batch.requested} requested · {batch.region_label || "mixed"}
                {batch.complexity_label ? ` · ${batch.complexity_label} names` : ""}
                {batch.created_at ? ` · started ${formatWhen(batch.created_at)}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!batch.running && awaiting > 0 && (
                <Button
                  size="sm"
                  onClick={() => onReview?.(batch.region)}
                  data-testid="face-run-review-btn"
                >
                  Review {awaiting} awaiting review
                </Button>
              )}
              {batch.retryable && batch.remaining > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => retry(batch.batch_id)}
                  data-testid="face-run-retry-btn"
                >
                  <RotateCw className="w-4 h-4 mr-1" />
                  Try again for the missing {batch.remaining}
                </Button>
              )}
              {!batch.running && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    localStorage.removeItem(WATCH_KEY);
                    setBatch(null);
                  }}
                  data-testid="face-run-dismiss"
                >
                  Dismiss
                </Button>
              )}
            </div>
          </div>

          <div className="h-1.5 bg-border mt-3">
            <div
              className={`h-full transition-all duration-500 ${
                batch.status === "complete" ? "bg-emerald-500" : "bg-primary"
              }`}
              style={{ width: `${batch.percent}%` }}
              data-testid="face-run-bar"
            />
          </div>

          {batch.paused && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="face-run-paused">
              The server restarted — the run picks up where it left off, nothing is lost.
            </p>
          )}

          {batch.failure_reason && (
            <p className="text-xs text-amber-400 mt-2" data-testid="face-run-reason">
              {batch.failure_reason}
            </p>
          )}

          {faces.length > 0 && !batch.running && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="face-run-produced">
              {faces.length} face{faces.length === 1 ? "" : "s"} from this run
              {awaiting ? ` · ${awaiting} awaiting review` : ""}
            </p>
          )}
        </div>
      )}

      {/* The two ways to generate, side by side with the difference spelled out */}
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
        <div className="p-4" data-testid="face-generate-set">
          <p className="font-semibold text-sm mb-1">Generate a set number</p>
          <p className="text-xs text-muted-foreground mb-3">
            For when you want more faces regardless of where the region stands.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="mb-1 block text-xs">Region</Label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="h-9 px-2 bg-background border border-border text-sm min-w-[10rem]"
                data-testid="face-region-select"
              >
                {(options?.regions || [{ key: "uk", label: "UK" }]).map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Name complexity</Label>
              <select
                value={complexity}
                onChange={(e) => setComplexity(e.target.value)}
                className="h-9 px-2 bg-background border border-border text-sm min-w-[8rem]"
                data-testid="face-complexity-select"
              >
                {(options?.complexities || [{ key: "simple", label: "Simple" }]).map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">How many</Label>
              <Input
                type="number"
                min={1}
                max={options?.max_count || 50}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="w-20 h-9 bg-background"
                data-testid="face-generate-count"
              />
            </div>
            <Button
              onClick={generate}
              disabled={starting || batch?.running}
              className="bg-primary text-primary-foreground h-9"
              data-testid="face-generate-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {batch?.running ? "Run in progress" : "Generate"}
            </Button>
          </div>
          <p className="text-xs text-foreground mt-3" data-testid="face-generate-sentence">
            This will generate {count} new {regionLabel(region)} face
            {Number(count) === 1 ? "" : "s"} with {complexityLabel} names. They go to{" "}
            <span className="font-semibold">awaiting review</span> — nothing is live until you
            approve it.
          </p>
        </div>

        <div className="p-4" data-testid="face-generate-topup">
          <p className="font-semibold text-sm mb-1">Top up a region to its target</p>
          <p className="text-xs text-muted-foreground mb-3">
            Generates only the shortfall for {regionLabel(region)}, so you never over-generate.
          </p>
          <Button
            variant="outline"
            className="h-9"
            onClick={topUp}
            disabled={!plan || plan.will_generate === 0 || starting || batch?.running}
            data-testid="face-topup-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {plan?.will_generate
              ? `Top up ${plan.will_generate}`
              : "Nothing to top up"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 ml-2"
            onClick={loadPlan}
            data-testid="face-topup-refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <p className="text-xs text-foreground mt-3" data-testid="face-topup-sentence">
            {plan ? plan.sentence : "Checking where this region stands…"}
            {plan?.will_generate
              ? ` They go to awaiting review with ${complexityLabel} names.`
              : ""}
          </p>
        </div>
      </div>

      {/* Batch history — the answer to "did they get made?" */}
      <div className="border-t border-border">
        <button
          onClick={() => setShowHistory((s) => !s)}
          className="w-full flex items-center gap-2 p-3 text-sm text-muted-foreground hover:text-foreground"
          data-testid="face-history-toggle"
        >
          <History className="w-4 h-4" />
          Batch history ({history.length})
        </button>
        {showHistory && (
          <div className="px-3 pb-3" data-testid="face-history-list">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No runs recorded yet.</p>
            ) : (
              <div className="divide-y divide-border">
                {history.map((b) => (
                  <div key={b.batch_id} className="py-2" data-testid={`face-history-${b.batch_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-xs">
                        <span className={`font-semibold ${STATE_TONE[b.status] || ""}`}>
                          {STATE_WORDS[b.status] || b.status}
                        </span>
                        <span className="text-muted-foreground">
                          {" · "}
                          {b.created} of {b.requested} created
                          {b.failed ? `, ${b.failed} failed` : ""}
                          {" · "}
                          {b.region_label || "mixed"}
                          {b.complexity_label ? ` · ${b.complexity_label}` : ""}
                          {" · "}
                          {formatWhen(b.created_at)}
                          {b.created_by ? ` · ${b.created_by}` : ""}
                          {b.kind && b.kind !== "generate" ? ` · ${b.kind}` : ""}
                          {b.resumes ? ` · resumed ${b.resumes}×` : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => openRunFaces(b.batch_id)}
                          data-testid={`face-history-open-${b.batch_id}`}
                        >
                          {openRun === b.batch_id ? "Hide faces" : "Show faces"}
                        </Button>
                        {b.retryable && b.remaining > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => retry(b.batch_id)}
                            data-testid={`face-history-retry-${b.batch_id}`}
                          >
                            Try again ({b.remaining})
                          </Button>
                        )}
                      </div>
                    </div>
                    {b.failure_reason && (
                      <p className="text-xs text-amber-400 mt-1">{b.failure_reason}</p>
                    )}
                    {openRun === b.batch_id && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {runFaces.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            This run produced no faces.
                          </p>
                        ) : (
                          runFaces.map((f) => (
                            <img
                              key={f.face_id}
                              src={`${process.env.REACT_APP_BACKEND_URL}${f.image_url}`}
                              alt={`${f.first_name || ""} ${f.last_name || ""}`}
                              title={`${f.first_name || ""} ${f.last_name || ""} · ${f.status}`}
                              className="w-12 h-12 object-cover border border-border"
                              loading="lazy"
                            />
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FaceGenerationPanel;
